/**
 * KAN-834: карточка клиента вкладки "Внимание": имя, организация, причина,
 * время, кнопка "Позвонить". Нажатие на карточку открывает клиента.
 * KAN-851: значок теплоты лида; название организации открывает её карточку
 * (ссылка клиента растянута на всю карточку, ссылка организации поверх).
 * KAN-852: подпись "чей клиент" (ничей / ваш / у ФИО) и кнопки "Взять в работу"
 * и "Назначить" (useMobileAttentionWork); после взятия строка уходит из списка.
 * KAN-857: Telegram-аватар клиента слева от имени (без картинки инициалы).
 */
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { ClientAvatar } from '@/components/clients/ClientAvatar';
import { ManagerCallButton } from '@/components/clients/ManagerCallButton';
import { AttentionDoneDialog } from '@/components/dashboard/AttentionDoneDialog';
import { attentionItemTitle, formatAttentionDate, splitOfferedText } from '@/components/dashboard/attentionFormat';
import { LeadTemperatureBadge } from '@/components/leads/LeadTemperatureBadge';
import { MobileCompanyLink } from '@/components/mobile/MobileCompanyLink';
import { MobileOwnerBadge } from '@/components/mobile/MobileOwnerBadge';
import { MobileWorkButtons } from '@/components/mobile/MobileWorkButtons';
import { useMobileAttentionWork } from '@/hooks/useMobileWork';
import { mobileClientPath } from '@/lib/mobileApp';
import type { AttentionItem } from '@/services/api/dashboard';

export function MobileAttentionCard({ item }: { item: AttentionItem }): JSX.Element {
  const showPlace = !!(item.client_company?.name || item.company) && !!item.name;
  const reason = splitOfferedText(item.reason_text);
  const work = useMobileAttentionWork(item);
  const hasWork = work.canTake || work.canAssign;
  return (
    <li className="rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="mobile-attention-card">
      <div className="relative flex items-start gap-3 p-4 active:bg-gray-50">
        {/* KAN-864: above the stretched card link (like MobileCompanyLink): the picture opens large. */}
        <span className="relative z-10 shrink-0">
          <ClientAvatar avatarUrl={item.avatar_url} name={attentionItemTitle(item)} size={40} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <Link
              to={mobileClientPath(item.client_id)}
              className="truncate text-base font-semibold text-gray-900 after:absolute after:inset-0 after:content-['']"
            >
              {attentionItemTitle(item)}
            </Link>
            <span className="shrink-0 text-xs text-gray-500">{formatAttentionDate(item.since)}</span>
          </div>
          <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-2">
            <MobileOwnerBadge label={work.ownerLabel} testId={`mobile-owner-${item.client_id}`} />
            <LeadTemperatureBadge level={item.lead_temperature} />
            {showPlace && (
              <MobileCompanyLink company={item.client_company} fallback={item.company} className="truncate text-sm" />
            )}
          </div>
          {reason && <p className="mt-1 text-sm text-gray-800">{reason}</p>}
        </div>
        <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
      </div>
      {(hasWork || item.phone) && (
        <div className="space-y-2 border-t border-gray-100 px-4 py-3">
          <MobileWorkButtons work={work} disabled={work.pending} testIdPrefix={`mobile-work-${item.client_id}`} />
          {item.phone && (
            <ManagerCallButton
              clientId={item.client_id}
              phone={item.phone}
              size="lg"
              targetMode="onClick"
              testId={`mobile-call-${item.client_id}`}
            />
          )}
        </div>
      )}
      <AttentionDoneDialog {...work.dialog} />
    </li>
  );
}
